import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Package, 
  TrendingUp,
  TrendingDown,
  Star,
  AlertTriangle,
  RefreshCw,
  Download,
  Target,
  FileSpreadsheet,
  Layers,
  BarChart3,
  Percent
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProductMixDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-dashboards/product-mix`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load ProductMix dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/product-mix/export`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const handleExportAll = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/product-mix/export-all`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `product_mix_${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  const formatNumber = (value) => {
    if (typeof value === 'string') return value;
    return value?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="product-mix-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="product-mix-no-data">
        <Package className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Analysis Available</h2>
        <p className="text-slate-500">{data?.message || 'Please run the ReasonProductMix agent first.'}</p>
        <Button onClick={() => window.location.href = '/agents'}>Go to Agents</Button>
      </div>
    );
  }

  const overview = data?.productOverview || {};
  const lifecycle = data?.lifecycleBuckets || {};
  const categoryPerf = data?.categoryPerformance || [];
  const brandPerf = data?.brandPerformance || [];
  const priceBands = data?.priceBandAnalysis || [];
  const assortmentGaps = data?.assortmentGaps || [];
  const recommendations = data?.recommendations || [];
  const actionPlan = data?.actionPlan || {};

  const COLORS = ['#f97316', '#22c55e', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];

  // Prepare lifecycle chart data
  const lifecycleData = [
    { name: 'Hero', count: lifecycle.hero?.skuCount || 0, revenue: lifecycle.hero?.revenue || 0 },
    { name: 'Core', count: lifecycle.core?.skuCount || 0, revenue: lifecycle.core?.revenue || 0 },
    { name: 'Slow', count: lifecycle.slow?.skuCount || 0, revenue: lifecycle.slow?.revenue || 0 },
    { name: 'Exit', count: lifecycle.exit?.skuCount || 0, revenue: lifecycle.exit?.blockedCapital || 0 }
  ];

  return (
    <div className="space-y-6" data-testid="product-mix-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Package className="h-8 w-8 text-orange-600" />
            <TranslatedText text="ProductMix Dashboard" cacheKey="productmix:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Product Lifecycle & Assortment Intelligence" cacheKey="productmix:subtitle" />
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExportAll('all_analysis', 'Full Analysis')}>
            <Download className="h-4 w-4 mr-2" />Export All
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      <Card className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 border-orange-200">
        <CardHeader><CardTitle><TranslatedText text="Executive Summary" cacheKey="productmix:execsummary" /></CardTitle></CardHeader>
        <CardContent>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text={data?.summary} cacheKey="productmix:summary" />
          </p>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <p className="text-xs text-orange-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total SKUs" cacheKey="productmix:total_skus" /></p>
            <p className="text-xl font-bold text-orange-900">{formatNumber(overview.totalSKUs)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <p className="text-xs text-green-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Active SKUs" cacheKey="productmix:active_skus" /></p>
            <p className="text-xl font-bold text-green-900">{formatNumber(overview.activeSKUs)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <p className="text-xs text-red-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Dormant SKUs" cacheKey="productmix:dormant_skus" /></p>
            <p className="text-xl font-bold text-red-900">{formatNumber(overview.dormantSKUs)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-xs text-blue-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Avg SKU Revenue" cacheKey="productmix:avg_revenue" /></p>
            <p className="text-xl font-bold text-blue-900">{formatCurrency(overview.avgSKURevenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Avg Margin" cacheKey="productmix:avg_margin" /></p>
            <p className="text-xl font-bold text-purple-900">{overview.avgMargin || 'N/A'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Top 5 Recommendations */}
      <Card className="border-orange-200 bg-orange-50/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <Target className="h-5 w-5" />
              <TranslatedText text="Top 5 Product Recommendations" cacheKey="productmix:recommendations_title" />
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExportAll('recommendations', 'All Recommendations')} disabled={exporting === 'recommendations'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Download All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recommendations.slice(0, 5).map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  idx === 0 ? 'bg-orange-500' : idx === 1 ? 'bg-blue-500' : 'bg-slate-500'
                }`}>{idx + 1}</span>
                <div className="flex-1">
                  <p className="font-medium">{rec.description || rec.action || rec.text || rec}</p>
                  {rec.expectedImpact && <p className="text-sm text-orange-600 mt-1">Impact: {rec.expectedImpact}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lifecycle Buckets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Product Lifecycle Distribution
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={lifecycleData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#f97316" name="SKU Count" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-4 gap-2 mt-4">
              <div className="p-2 bg-green-50 rounded text-center">
                <Star className="h-4 w-4 text-green-600 mx-auto mb-1" />
                <p className="text-xs font-medium text-green-700">Hero</p>
                <p className="text-lg font-bold text-green-900">{lifecycle.hero?.skuCount || 0}</p>
                <p className="text-xs text-green-600">{lifecycle.hero?.revenueShare}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded text-center">
                <BarChart3 className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                <p className="text-xs font-medium text-blue-700">Core</p>
                <p className="text-lg font-bold text-blue-900">{lifecycle.core?.skuCount || 0}</p>
                <p className="text-xs text-blue-600">{lifecycle.core?.revenueShare}</p>
              </div>
              <div className="p-2 bg-yellow-50 rounded text-center">
                <TrendingDown className="h-4 w-4 text-yellow-600 mx-auto mb-1" />
                <p className="text-xs font-medium text-yellow-700">Slow</p>
                <p className="text-lg font-bold text-yellow-900">{lifecycle.slow?.skuCount || 0}</p>
                <p className="text-xs text-yellow-600">{lifecycle.slow?.revenueShare}</p>
              </div>
              <div className="p-2 bg-red-50 rounded text-center">
                <AlertTriangle className="h-4 w-4 text-red-600 mx-auto mb-1" />
                <p className="text-xs font-medium text-red-700">Exit</p>
                <p className="text-lg font-bold text-red-900">{lifecycle.exit?.skuCount || 0}</p>
                <p className="text-xs text-red-600">{formatCurrency(lifecycle.exit?.blockedCapital)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hero SKUs */}
        <Card className="border-green-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-green-700 flex items-center gap-2">
                <Star className="h-5 w-5" />
                Hero SKUs (Top Performers)
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('hero_skus', 'Hero SKUs')} disabled={exporting === 'hero_skus'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(lifecycle.hero?.topSKUs || []).slice(0, 10).map((sku, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-100">
                  <div>
                    <p className="font-medium text-sm">{sku.name || sku.skuId}</p>
                    <p className="text-xs text-slate-500">{sku.skuId}</p>
                  </div>
                  <span className="text-sm font-semibold text-green-600">{formatCurrency(sku.revenue)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Performance */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Category Performance</CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('category_performance', 'Category Performance')} disabled={exporting === 'category_performance'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {categoryPerf.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryPerf.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                <YAxis dataKey="category" type="category" width={100} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="revenue" fill="#f97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-slate-500 text-center py-8">No category data available</p>
          )}
        </CardContent>
      </Card>

      {/* Assortment Gaps */}
      {assortmentGaps.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader>
            <CardTitle className="text-yellow-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Assortment Gaps (Missing Revenue Opportunities)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-yellow-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Store</th>
                    <th className="px-3 py-2 text-right font-medium">Missing SKUs</th>
                    <th className="px-3 py-2 text-right font-medium">Missed Revenue</th>
                    <th className="px-3 py-2 text-left font-medium">Top Missing SKUs</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {assortmentGaps.slice(0, 10).map((gap, idx) => (
                    <tr key={idx} className="hover:bg-yellow-50/50">
                      <td className="px-3 py-2 font-medium">{gap.storeName || gap.storeId}</td>
                      <td className="px-3 py-2 text-right text-red-600">{gap.missingSKUs}</td>
                      <td className="px-3 py-2 text-right text-orange-600 font-medium">{formatCurrency(gap.missedRevenue)}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{(gap.topMissingSKUs || []).slice(0, 3).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Prioritized Action Plan</CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExportAll('action_plan', 'Action Plan')} disabled={exporting === 'action_plan'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-red-800 mb-3">P0 - Immediate</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P0_Immediate || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h4 className="font-bold text-yellow-800 mb-3">P1 - This Week</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P1_ThisWeek || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h4 className="font-bold text-green-800 mb-3">P2 - Next Cycle</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P2_NextCycle || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export All Products */}
      <Card className="bg-slate-50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Export Full Product Data</h3>
              <p className="text-sm text-slate-500">Download complete product master for analysis</p>
            </div>
            <Button onClick={() => handleExport('all_products', 'All Products')} disabled={exporting === 'all_products'}>
              <Download className="h-4 w-4 mr-2" />
              {exporting === 'all_products' ? 'Exporting...' : 'Export All Products'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductMixDashboard;
